import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination, Avatar, Report } from "../../components"

/* Widgets */
import { Filter, PlayerView, Watch } from "../../widgets/Player"

/* Access */
import { isSuperAdmin } from "../../constants/access"

/* REST API */
import { list, toggle } from "../../api/Player"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { SearchIcon, CloseIcon, FilterIcon, EyeIcon, CopyIcon, DotsIcon, PlayersIcon, VipIcon, TesterIcon, BlockIcon, DebitIcon, CreditIcon, NetIcon, OperatorIcon, TableIcon, RoundIcon, SessionIcon, WalletIcon, CalendarIcon, BellIcon } from "../../widgets/Player/PlayerIcons"

/* Utils */
import { getAvatar, getGame, getCurrency, isEmpty, formatCount, formatDay, formatTime, formatAgo } from "../../widgets/Player/PlayerUtils"

/* Translate */
import { t } from "../../translates/translate"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Export Excel */
import { downloadExcel as downloadXlsx } from "react-export-table-to-excel-xlsx"

/* Export PDF */
import { jsPDF } from "jspdf"
import html2canvas from "html2canvas"

/* Timezone — окно по умолчанию строим в ВЫБРАННОЙ зоне */
import { nowInTz } from "../../helpers/timezone"

/* Moment */
import moment from "moment"


/* PAGE SIZE — THE SAME NUMBER THE CONTROLLER USES */
const SIZE = 20

/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400

/* EMPTY ANSWER — THE SAME SHAPE THE CONTROLLER SENDS */
const EMPTY = {
    totals: { debit: 0, credit: 0, ggr: 0, rounds: 0 },
    stats: { players: 0, testers: 0, vips: 0, sessions: 0 },
}


/* Page Players */
class Players extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            search: "",
            operators: [],
            tables: [],
            selected: "today",
            start: nowInTz().startOf("day").toDate(),
            end: nowInTz().endOf("day").toDate(),
            testers: false,
            vips: true,
            sort: "date",
            totals: EMPTY.totals,
            stats: EMPTY.stats,
            canEdit: false,
            downloading: null,
            menu: null,
        }

        this._timer = null
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
        document.addEventListener("click", this.closeMenu)
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
        document.removeEventListener("click", this.closeMenu)
    }

    /* Open The Menu Of A Row — the click must not reach the row and open the card of the player */
    openMenu = (event, index) => {

        event.stopPropagation()

        this.setState({ menu: this.state.menu === index ? null : index })
    }

    /* Close The Menu Of A Row — any click outside of it closes the menu */
    closeMenu = () => {
        if (this.state.menu !== null) {
            this.setState({ menu: null })
        }
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Parameters Of The Request — the list, the report and the export read the very same filter */
    getParams = () => {

        /* Fields */
        const { search, operators, tables, start, end, testers, vips, sort } = this.state

        /* Send Result */
        return {
            search,
            operators: operators.map((item) => item.id).join(","),
            tables: tables.map((item) => item.id).join(","),
            start: start ? moment(start).format("YYYY-MM-DD HH:mm:ss") : "",
            end: end ? moment(end).format("YYYY-MM-DD HH:mm:ss") : "",
            testers,
            vips,
            sort,
        }
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list({ page, ...this.getParams() }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    totals: response.data.totals ? response.data.totals : EMPTY.totals,
                    stats: response.data.stats ? response.data.stats : EMPTY.stats,
                    canEdit: utils.checkBoolean(response.data.canEdit),
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0, ...EMPTY })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0, ...EMPTY })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, ...EMPTY })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, ...EMPTY })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.apply(), DELAY)
    }

    /* Apply Filter */
    applyFilter = ({ selected, start, end, operators, tables, testers, vips, sort }) => {
        this.setState({ selected, start, end, operators, tables, testers, vips, sort }, () => this.apply())
    }

    /* Apply Action — every filter restarts from the first page */
    apply = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/player/1")
            return
        }

        this.load()
    }

    /* Copy Action — the code of the player goes to the clipboard straight from the row */
    copy = (value) => {

        /* Check Value */
        if (!value || !navigator.clipboard) {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(String(value)).then(() => {
            Swal.fire(utils.notification(t("plr.codeCopied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
        })

    }

    /* Warning Of A Flag — what the change does to the player */
    warning = (field, value) => {

        if (field === "isBlocked") {
            return value === 1 ? t("player.changeBlockWarning") : t("player.changeUnblockWarning")
        }

        return field === "isVip" ? t("player.changeVipWarning") : t("player.changeWarning")
    }

    /* Toggle A Flag Of A Player (isTester / isVip / isBlocked) */
    toggle = (item, value, field = "isTester") => {

        /* Fields */
        const { data } = this.state

        /* Close Menu */
        this.setState({ menu: null })

        /* Confirm */
        Swal.fire({
            title: t("player.changeConfirm"),
            text: this.warning(field, value),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#03a9f4",
            reverseButtons: true,
            confirmButtonText: t("common.yes"),
            cancelButtonText: t("common.no"),
        }).then((result) => {
            if (result.isConfirmed) {

                /* Send to REST API */
                toggle({ id: item.player, value, field }).then((response) => {
                    if (response.status === 200) {

                        /* Get Index */
                        const index = data.findIndex((element) => element.player === item.player)

                        /* Check Index */
                        if (index > -1) {
                            data[index][field] = value
                            this.setState({ data })
                        }

                        Swal.fire(utils.notification(t("player.statusChanged"), "success"))

                    }
                    else if (response.status === 401 || response.status === 403) {
                        Swal.fire(utils.notification(t("swal.noAccess"), "error"))
                    }
                    else if (response.status === 498) {
                        Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                    }
                    else {
                        Swal.fire(utils.notification(t("swal.generic"), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                })

            }
        })

    }

    /* Count Of The Active Filters — the badge of the filter button */
    getCount = () => {

        /* Fields */
        const { selected, operators, tables, testers, vips, sort } = this.state
        let count = 0

        if (selected !== "today") {
            count = count + 1
        }

        if (operators.length > 0) {
            count = count + 1
        }

        if (tables.length > 0) {
            count = count + 1
        }

        if (testers) {
            count = count + 1
        }

        if (!vips) {
            count = count + 1
        }

        if (sort !== "date") {
            count = count + 1
        }

        /* Send Result */
        return count
    }

    /* Title Of The Chosen Period — it stands right under the name of the section */
    getPeriod = () => {

        /* Fields */
        const { selected, start, end } = this.state

        /* Titles */
        const titles = {
            "3hours": t("player.last3h"),
            "6hours": t("player.last6h"),
            "12hours": t("player.last12h"),
            "today": t("player.today"),
            "yesterday": t("player.yesterday"),
            "7days": t("player.last7days"),
            "14days": t("player.last14days"),
            "30days": t("player.last30days"),
            "month": t("player.thisMonth"),
            "prevmonth": t("player.prevMonth"),
            "period": t("player.filterPeriod"),
            "all": t("player.allTime"),
        }

        /* Check Period */
        if (selected === "all" || !start || !end) {
            return { title: t("player.allTime"), text: "" }
        }

        /* Fields */
        const from = moment(new Date(start))
        const to = moment(new Date(end))

        /* One Day — the date is written once, only the hours differ */
        const text = from.isSame(to, "day")
            ? `${from.format("DD.MM.YYYY")}, ${from.format("HH:mm")} — ${to.format("HH:mm")}`
            : `${from.format("DD.MM.YYYY HH:mm")} — ${to.format("DD.MM.YYYY HH:mm")}`

        /* Send Result */
        return { title: titles[selected] ? titles[selected] : t("player.filterPeriod"), text }
    }

    /* Draw The Chosen Period — it stands under the header, the title alone stays on the top line */
    _periodBar = () => {

        /* Fields */
        const period = this.getPeriod()

        return (
            <div className="plr--period-bar">
                <CalendarIcon size={13} />
                <b>{period.title}</b>
                {period.text !== "" && <span>{period.text}</span>}
            </div>
        )
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="plr--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("player.search")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="plr--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Filter Button */
    _filter = () => {

        /* Fields */
        const { selected, start, end, operators, tables, testers, vips, sort } = this.state
        const count = this.getCount()

        return (
            <div
                onClick={() => this._filterModal.open({ selected, start, end, operators, tables, testers, vips, sort })}
                className={`plr--filter ${count > 0 ? "active" : ""}`}
            >
                <FilterIcon size={15} />
                <span>{t("gtx.filter")}</span>
                {count > 0 && <div className="plr--filter-count">{count}</div>}
            </div>
        )
    }

    /* Контроль входа — список игроков, о заходе которых пишем в телеграм. Только суперадмин */
    _watch = () => {

        /* Fields */
        const { admin } = this.props

        /* Check Access */
        if (!isSuperAdmin(admin)) {
            return null
        }

        return (
            <div onClick={() => this._watchModal.open()} className="plr--watch-button">
                <BellIcon size={13} />
                <span>{t("plr.watchButton")}</span>
            </div>
        )
    }

    /* Draw Report Buttons */
    _report = () => {
        return <Report excel={this.downloadXLSX} pdf={this.downloadPDF} downloading={this.state.downloading} />
    }

    /* Draw One Number Of The Filter */
    _stat = (icon, title, value, tone = "") => {

        return (
            <div className={`plr--stat ${tone}`}>

                <div className="plr--stat-icon">{icon}</div>

                <div className="plr--stat-text">
                    <div className="plr--stat-title">{title}</div>
                    <div className="plr--stat-value">{value}</div>
                </div>

            </div>
        )
    }

    /* Draw Numbers — what the current filter really holds, every number stands in one row */
    _numbers = () => {

        /* Fields */
        const { count, stats, totals } = this.state
        const debit = Number(totals.debit) || 0
        const credit = Number(totals.credit) || 0
        const net = debit - credit

        return (
            <>

                <div className="plr--stats compact">

                    {this._stat(<PlayersIcon />, t("plr.playersCount"), formatCount(count), "blue")}
                    {this._stat(<VipIcon />, t("gtx.vipsCount"), formatCount(stats.vips), "gold")}
                    {this._stat(<TesterIcon />, t("plr.testersCount"), formatCount(stats.testers), "violet")}
                    {this._stat(<SessionIcon />, t("plr.sessionsCount"), formatCount(stats.sessions), "cyan")}
                    {this._stat(<RoundIcon />, t("plr.roundsCount"), formatCount(totals.rounds), "orange")}

                </div>

                <div className="plr--stats compact">

                    {this._stat(<DebitIcon />, t("gtx.totalDebit"), utils.convertor(debit, "USD", 1), "red")}
                    {this._stat(<CreditIcon />, t("gtx.totalCredit"), utils.convertor(credit, "USD", 1), "green")}
                    {this._stat(<NetIcon />, "GGR", utils.convertor(net, "USD", 1), net >= 0 ? "gold" : "red")}

                </div>

            </>
        )
    }

    /* Draw The Menu Of A Row — the test and the VIP marks live there */
    _menu = (item, index) => {

        /* Fields */
        const { menu, canEdit } = this.state

        /* Check Rights — a viewer only reads */
        if (!canEdit) {
            return null
        }

        return (
            <div className="plr--menu">

                <div
                    onClick={(event) => this.openMenu(event, index)}
                    className={`plr--action ${menu === index ? "active" : ""}`}
                    title={t("plr.more")}
                >
                    <DotsIcon size={16} />
                </div>

                {menu === index &&
                    <div onClick={(event) => event.stopPropagation()} className="plr--menu-list">

                        <div onClick={() => this.toggle(item, parseInt(item.isTester) === 1 ? 0 : 1)} className="plr--menu-item">
                            <TesterIcon size={14} />
                            <span>{parseInt(item.isTester) === 1 ? t("player.removeTester") : t("player.setTester")}</span>
                        </div>

                        <div onClick={() => this.toggle(item, parseInt(item.isVip) === 1 ? 0 : 1, "isVip")} className="plr--menu-item">
                            <VipIcon size={14} />
                            <span>{parseInt(item.isVip) === 1 ? t("player.removeVip") : t("player.setVip")}</span>
                        </div>

                        <div onClick={() => this.toggle(item, parseInt(item.isBlocked) === 1 ? 0 : 1, "isBlocked")} className={`plr--menu-item ${parseInt(item.isBlocked) === 1 ? "" : "danger"}`}>
                            <BlockIcon size={14} />
                            <span>{parseInt(item.isBlocked) === 1 ? t("player.removeBlocked") : t("player.setBlocked")}</span>
                        </div>

                    </div>
                }

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="plr--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="plr--page">

                {/* Head */}
                <div className="plr--table-header">
                    <div className="plr--column plr--col-number">#</div>
                    <div className="plr--column plr--col-player">{t("player.player")}</div>
                    <div className="plr--column plr--col-game">{t("player.lastGame")}</div>
                    <div className="plr--column plr--col-activity">{t("plr.activity")}</div>
                    <div className="plr--column plr--col-money">GGR</div>
                    <div className="plr--column plr--col-balance">{t("player.balance")}</div>
                    <div className="plr--column plr--col-date">{t("player.dateTime")}</div>
                    <div className="plr--column plr--col-actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="plr--empty">
                        <PlayersIcon size={40} />
                        <span>{search !== "" || this.getCount() > 0 ? t("gtx.nothingFound") : t("plr.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) => {

                    /* Fields */
                    const ggr = parseFloat(item.ggr) || 0

                    return (
                        <div onClick={() => this._playerView.open({ player: item.player, isTester: item.isTester })} className="plr--row" key={`${index}`}>

                            {/* Number */}
                            <div className="plr--column plr--col-number">{(page - 1) * SIZE + index + 1}</div>

                            {/* Player */}
                            <div className="plr--column plr--col-player">

                                <Avatar uri={getAvatar(item)} size={42} />

                                <div className="plr--player-text">

                                    <div className="plr--player-name">
                                        <span className="plr--player-value">{utils.checkValue(item.firstName, "—")}</span>
                                        {parseInt(item.isTester) === 1 && <span className="plr--tag tester">{t("gtx.tester")}</span>}
                                        {parseInt(item.isVip) === 1 && <span className="plr--tag vip">VIP</span>}
                                        {parseInt(item.isBlocked) === 1 && <span className="plr--tag blocked">{t("player.blocked")}</span>}
                                    </div>

                                    <div className="plr--player-code">{item.player}</div>

                                    <div className="plr--operator" title={utils.checkValue(item.operator, "")}>
                                        <OperatorIcon size={11} />
                                        <span>{utils.checkValue(item.operator, "—")}</span>
                                    </div>

                                </div>

                            </div>

                            {/* Game */}
                            <div className="plr--column plr--col-game">
                                <div className="plr--game-name">{getGame(item)}</div>
                                <div className="plr--game-note">
                                    <TableIcon size={11} />
                                    {utils.checkValue(item.slug, "—")}
                                </div>
                            </div>

                            {/* Activity */}
                            <div className="plr--column plr--col-activity">

                                <div className="plr--pill">
                                    <RoundIcon size={11} />
                                    <span>{formatCount(item.rounds)}</span>
                                </div>

                                <div className="plr--pill cyan">
                                    <SessionIcon size={11} />
                                    <span>{formatCount(item.sessions)}</span>
                                </div>

                            </div>

                            {/* Money */}
                            <div className="plr--column plr--col-money">

                                <div className={`plr--ggr ${ggr > 0 ? "green" : ""} ${ggr < 0 ? "red" : ""}`}>
                                    {ggr > 0 && "+ "}
                                    {ggr < 0 && "- "}
                                    {utils.convertor(Math.abs(ggr), "USD", 1)}
                                </div>

                                <div className="plr--money-row">
                                    <span className="plr--money debit">+ {utils.convertor(item.debit, "USD", 1)}</span>
                                    <span className="plr--money credit">- {utils.convertor(item.credit, "USD", 1)}</span>
                                </div>

                            </div>

                            {/* Balance */}
                            <div className="plr--column plr--col-balance">

                                <div className="plr--balance">
                                    <WalletIcon size={12} />
                                    <span>{isEmpty(item.balance) ? "—" : utils.convertor(parseFloat(item.balance), "", 0, " ")}</span>
                                </div>

                                <div className="plr--currency">{utils.checkValue(getCurrency(item.currency), "—")}</div>

                            </div>

                            {/* Date */}
                            <div className="plr--column plr--col-date">
                                <div className="plr--date">{formatDay(item.lastTx)}</div>
                                <div className="plr--time">{formatTime(item.lastTx)}</div>
                                <div className="plr--ago">{formatAgo(item.lastTx)}</div>
                            </div>

                            {/* Actions */}
                            <div onClick={(event) => event.stopPropagation()} className="plr--column plr--col-actions">

                                <div onClick={() => this._playerView.open({ player: item.player, isTester: item.isTester })} className="plr--action" title={t("player.viewStats")}>
                                    <EyeIcon />
                                </div>

                                <div onClick={() => this.copy(item.player)} className="plr--action" title={t("plr.copyCode")}>
                                    <CopyIcon size={15} />
                                </div>

                                {this._menu(item, index)}

                            </div>

                        </div>
                    )
                })}

                {/* Pagination */}
                <Pagination size={SIZE} page={page} total={count} onPageChange={(page) => navigate(`/player/${page}`)} />

            </div>
        )
    }

    /* Download XLSX */
    downloadXLSX = () => {

        /* Start Loading */
        this.setState({ downloading: "xlsx" })

        /* Fields */
        const period = this.getPeriod()
        const title = `${t("player.report")} ${period.title} ${period.text}`

        /* Send to REST API */
        list({ page: 1, ...this.getParams(), reporting: true }).then((response) => {
            if (response.status === 200) {

                /* Fields */
                const players = response.data.list

                /* Prepare Header */
                const header = {
                    name: t("player.reportName"),
                    players: t("player.reportCode"),
                    tester: t("player.reportTester"),
                    vip: t("player.reportVip"),
                    lastgame: t("player.reportLastGame"),
                    operator: t("player.reportOperator"),
                    currency: t("player.reportCurrency"),
                    rounds: t("player.roundsTab"),
                    lastLogin: t("player.reportLastSession"),
                    debit: "Debit",
                    credit: "Credit",
                    total: "TOTAL (GGR)",
                }
                const body = [{ name: title }, { name: "" }, header]

                /* Prepare Body */
                players.forEach((player) => {
                    body.push({
                        name: utils.checkValue(player.firstName, "-"),
                        players: utils.checkValue(player.player, "-"),
                        tester: parseInt(player.isTester) === 1 ? t("player.reportTesterYes") : t("player.reportTesterNo"),
                        vip: parseInt(player.isVip) === 1 ? t("player.reportTesterYes") : t("player.reportTesterNo"),
                        lastgame: getGame(player),
                        operator: utils.checkValue(player.operator, "-"),
                        currency: utils.checkValue(getCurrency(player.currency), "-"),
                        rounds: formatCount(player.rounds),
                        lastLogin: utils.checkValue(player.lastTx, "-"),
                        debit: utils.convertor(parseFloat(Math.abs(player.debit)), "$", 0, " "),
                        credit: utils.convertor(parseFloat(Math.abs(player.credit)), "$", 0, " "),
                        total: `${parseFloat(player.ggr) >= 0 ? "+" : "-"} ${utils.convertor(parseFloat(Math.abs(player.ggr)), "$", 0, " ")}`,
                    })
                })

                /* Call Download Excel */
                downloadXlsx({
                    fileName: title,
                    sheet: t("player.reportSheet"),
                    tablePayload: { header: [], body },
                })
            }
            else {
                Swal.fire(utils.notification(t("swal.generic"), "error", 2000))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error", 2000))
        }).finally(() => {
            this.setState({ downloading: null })
        })
    }

    /* Download PDF */
    downloadPDF = () => {

        /* Start Loading */
        this.setState({ downloading: "pdf" })

        /* Fields */
        const period = this.getPeriod()
        const title = `${t("player.report")} ${period.title} ${period.text}`

        /* Send to REST API */
        list({ page: 1, ...this.getParams(), reporting: true }).then((response) => {
            if (response.status === 200) {

                /* Build Off-screen Table (browser renders Cyrillic into canvas) */
                const container = document.createElement("div")
                container.style.position = "fixed"
                container.style.left = "-10000px"
                container.style.top = "0"
                container.style.width = "1400px"
                container.style.background = "#ffffff"
                container.innerHTML = this._reportHTML(title, response.data.list)
                document.body.appendChild(container)

                /* Rasterize And Slice Into A4 Landscape Pages */
                return html2canvas(container, { scale: 2, backgroundColor: "#ffffff" }).then((canvas) => {

                    document.body.removeChild(container)

                    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" })
                    const pageWidth = doc.internal.pageSize.getWidth()
                    const pageHeight = doc.internal.pageSize.getHeight()
                    const imgWidth = pageWidth
                    const imgHeight = (canvas.height * imgWidth) / canvas.width
                    const imgData = canvas.toDataURL("image/png")

                    let heightLeft = imgHeight
                    let position = 0

                    doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
                    heightLeft -= pageHeight

                    while (heightLeft > 0) {
                        position -= pageHeight
                        doc.addPage()
                        doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
                        heightLeft -= pageHeight
                    }

                    doc.save(`${title}.pdf`)
                })
            }
            else {
                Swal.fire(utils.notification(t("swal.generic"), "error", 2000))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error", 2000))
        }).finally(() => {
            this.setState({ downloading: null })
        })
    }

    /* Escape HTML (report values come from DB, guard against markup) */
    _escape = (value) => {
        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
    }

    /* Build Report HTML For PDF Capture */
    _reportHTML = (title, players) => {

        /* Columns — the same fields the XLSX report keeps */
        const columns = [
            { label: t("player.reportName"), value: (item) => utils.checkValue(item.firstName, "-"), align: "left" },
            { label: t("player.reportCode"), value: (item) => utils.checkValue(item.player, "-"), align: "left" },
            { label: t("player.reportTester"), value: (item) => (parseInt(item.isTester) === 1 ? t("player.reportTesterYes") : t("player.reportTesterNo")), align: "center" },
            { label: t("player.reportVip"), value: (item) => (parseInt(item.isVip) === 1 ? t("player.reportTesterYes") : t("player.reportTesterNo")), align: "center" },
            { label: t("player.reportLastGame"), value: (item) => getGame(item), align: "left" },
            { label: t("player.reportOperator"), value: (item) => utils.checkValue(item.operator, "-"), align: "left" },
            { label: t("player.reportCurrency"), value: (item) => utils.checkValue(getCurrency(item.currency), "-"), align: "center" },
            { label: t("player.roundsTab"), value: (item) => formatCount(item.rounds), align: "center" },
            { label: t("player.reportLastSession"), value: (item) => utils.checkValue(item.lastTx, "-"), align: "left" },
            { label: "Debit", value: (item) => utils.convertor(parseFloat(Math.abs(item.debit)), "$", 0, " "), align: "right" },
            { label: "Credit", value: (item) => utils.convertor(parseFloat(Math.abs(item.credit)), "$", 0, " "), align: "right" },
            { label: "TOTAL (GGR)", value: (item) => `${parseFloat(item.ggr) >= 0 ? "+" : "-"} ${utils.convertor(parseFloat(Math.abs(item.ggr)), "$", 0, " ")}`, color: (item) => (parseFloat(item.ggr) >= 0 ? "#4c9a2a" : "#d32f2f"), align: "right" },
        ]

        /* Header */
        const head = columns.map((column) =>
            `<th style="padding:8px 10px; text-align:${column.align}; border-bottom:2px solid #333; font-weight:700; white-space:nowrap;">${this._escape(column.label)}</th>`
        ).join("")

        /* Body */
        const rows = players.map((item, index) => {
            const background = index % 2 === 0 ? "#ffffff" : "#f6f6f6"
            const cells = columns.map((column) => {
                const color = column.color ? `color:${column.color(item)}; font-weight:600;` : ""
                return `<td style="padding:7px 10px; text-align:${column.align}; border-bottom:1px solid #e5e5e5; white-space:nowrap; ${color}">${this._escape(column.value(item))}</td>`
            }).join("")
            return `<tr style="background:${background};">${cells}</tr>`
        }).join("")

        return `
            <div style="padding:24px; font-family: Arial, sans-serif; color:#111;">
                <div style="font-size:18px; font-weight:700; margin-bottom:16px;">${this._escape(title)}</div>
                <table style="width:100%; border-collapse:collapse; font-size:12px;">
                    <thead><tr>${head}</tr></thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `
    }

    render = () => {

        /* Fields */
        const { status } = this.state

        return (
            <div className="page">

                <div className="page-header plr--header">

                    <h1>{t("player.title")}</h1>

                    {status === "ok" &&
                        <div className="plr--tools">

                            {/* Draw Search */}
                            {this._search()}

                            {/* Draw Filter Button */}
                            {this._filter()}

                            {/* Draw Report Buttons */}
                            {this._report()}

                        </div>
                    }

                </div>

                {/* Draw The Chosen Period */}
                {status === "ok" &&
                    <div className="plr--period-row">
                        {this._periodBar()}
                        {this._watch()}
                    </div>
                }

                {/* Draw Numbers */}
                {status === "ok" && this._numbers()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw Player Modal */}
                <PlayerView ref={(ref) => this._playerView = ref} />

                {/* Draw Filter Modal */}
                <Filter apply={this.applyFilter} ref={(ref) => this._filterModal = ref} />

                {/* Draw Watch Modal */}
                <Watch ref={(ref) => this._watchModal = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Players))
